import React, { useState, useEffect } from 'react';
import organisationSettingsService, { PowerPointSettings } from '../../services/organisationSettingsService';
import { useTheme } from '../../contexts/ThemeContext';
import './PowerPointSettingsForm.css';

interface PowerPointSettingsFormProps {
  currentSettings?: PowerPointSettings;
  onUpdate?: (settings: PowerPointSettings) => void;
  onError?: (error: string) => void;
}

const FONT_OPTIONS = [
  'Arial',
  'Calibri',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Verdana',
  'Segoe UI',
  'Tahoma',
  'Comic Sans MS',
  'Courier New'
];

const PowerPointSettingsForm: React.FC<PowerPointSettingsFormProps> = ({
  currentSettings,
  onUpdate,
  onError
}) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState<PowerPointSettings>({
    fontFamily: currentSettings?.fontFamily || 'Arial',
    fontSizeTitle: currentSettings?.fontSizeTitle || 36,
    fontSizeHeading: currentSettings?.fontSizeHeading || 28,
    fontSizeBody: currentSettings?.fontSizeBody || 16,
    titleColor: currentSettings?.titleColor || '#2a4065',
    headingColor: currentSettings?.headingColor || '#1a2845',
    bodyColor: currentSettings?.bodyColor || '#333333',
    coverBodyColor: currentSettings?.coverBodyColor || '#ffffff',
    backgroundCover: currentSettings?.backgroundCover,
    backgroundSplit: currentSettings?.backgroundSplit,
    backgroundText: currentSettings?.backgroundText
  });

  const [backgroundPreviews, setBackgroundPreviews] = useState<{
    cover?: string;
    split?: string;
    text?: string;
  }>({});

  const [pendingFiles, setPendingFiles] = useState<{
    cover?: File;
    split?: File;
    text?: File;
  }>({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Update form data when currentSettings change
  useEffect(() => {
    if (currentSettings) {
      setFormData({
        fontFamily: currentSettings.fontFamily || 'Arial',
        fontSizeTitle: currentSettings.fontSizeTitle || 36,
        fontSizeHeading: currentSettings.fontSizeHeading || 28,
        fontSizeBody: currentSettings.fontSizeBody || 16,
        titleColor: currentSettings.titleColor || '#2a4065',
        headingColor: currentSettings.headingColor || '#1a2845',
        bodyColor: currentSettings.bodyColor || '#333333',
        coverBodyColor: currentSettings.coverBodyColor || '#ffffff',
        backgroundCover: currentSettings.backgroundCover,
        backgroundSplit: currentSettings.backgroundSplit,
        backgroundText: currentSettings.backgroundText
      });
    }
  }, [currentSettings]);

  // Load existing background previews
  useEffect(() => {
    if (currentSettings) {
      // Remove trailing /api from REACT_APP_API_URL to avoid double /api in URL
      const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
      setBackgroundPreviews({
        cover: currentSettings.backgroundCover ? `${API_BASE}${currentSettings.backgroundCover}?t=${Date.now()}` : undefined,
        split: currentSettings.backgroundSplit ? `${API_BASE}${currentSettings.backgroundSplit}?t=${Date.now()}` : undefined,
        text: currentSettings.backgroundText ? `${API_BASE}${currentSettings.backgroundText}?t=${Date.now()}` : undefined
      });
    }
  }, [currentSettings]);

  const handleBackgroundSelect = (type: 'cover' | 'split' | 'text', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(`Invalid file type for ${type} background. Please use JPEG, PNG, or WebP.`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError(`${type} background file is too large. Maximum size is 10MB.`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    setPendingFiles(prev => ({ ...prev, [type]: file }));

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setBackgroundPreviews(prev => ({
        ...prev,
        [type]: e.target?.result as string
      }));
    };
    reader.readAsDataURL(file);
    setError(null);
  };

  const handleRemoveBackground = (type: 'cover' | 'split' | 'text') => {
    setPendingFiles(prev => {
      const updated = { ...prev };
      delete updated[type];
      return updated;
    });
    setBackgroundPreviews(prev => {
      const updated = { ...prev };
      delete updated[type];
      return updated;
    });
    const key = `background${type.charAt(0).toUpperCase()}${type.slice(1)}` as keyof PowerPointSettings;
    setFormData(prev => ({
      ...prev,
      [key]: undefined
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Upload any pending background files
      const uploadedBackgrounds: Partial<PowerPointSettings> = {};
      
      for (const [type, file] of Object.entries(pendingFiles)) {
        if (file) {
          const result = await organisationSettingsService.uploadPPTBackground(
            type as 'cover' | 'split' | 'text',
            file
          );
          const key = `background${type.charAt(0).toUpperCase()}${type.slice(1)}` as keyof PowerPointSettings;
          uploadedBackgrounds[key] = result.backgroundUrl as any;
        }
      }

      // Combine form data with uploaded backgrounds
      const finalSettings: PowerPointSettings = {
        ...formData,
        ...uploadedBackgrounds
      };

      // Update organization settings
      await organisationSettingsService.updateOrganisationSettings({
        powerPointSettings: finalSettings
      });

      setPendingFiles({});
      if (onUpdate) onUpdate(finalSettings);
      
      setSuccessMessage('PowerPoint settings saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to save PowerPoint settings';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (currentSettings) {
      setFormData(currentSettings);
      setPendingFiles({});
      // Remove trailing /api from REACT_APP_API_URL to avoid double /api in URL
      const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
      setBackgroundPreviews({
        cover: currentSettings.backgroundCover ? `${API_BASE}${currentSettings.backgroundCover}?t=${Date.now()}` : undefined,
        split: currentSettings.backgroundSplit ? `${API_BASE}${currentSettings.backgroundSplit}?t=${Date.now()}` : undefined,
        text: currentSettings.backgroundText ? `${API_BASE}${currentSettings.backgroundText}?t=${Date.now()}` : undefined
      });
    }
    setError(null);
    setSuccessMessage(null);
  };

  return (
    <div className="ppt-settings-form" data-theme={theme}>
      <form onSubmit={handleSubmit}>
        {/* Typography Section */}
        <div className="form-section">
          <h3>Typography</h3>
          <p className="form-description">
            Configure fonts and colors for your PowerPoint presentations
          </p>

          <div className="form-group">
            <label>Font Family</label>
            <span className="field-description">Applies to all slide text</span>
            <select
              value={formData.fontFamily}
              onChange={(e) => setFormData(prev => ({ ...prev, fontFamily: e.target.value }))}
              disabled={saving}
              className="form-select"
            >
              {FONT_OPTIONS.map(font => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>

          {/* Font Sizes and Colors Grid */}
          <div className="typography-grid">
            {/* TITLE */}
            <div className="typography-item">
              <h4>Title</h4>
              <div className="typography-controls">
                <div className="form-group">
                  <label>Size (pts)</label>
                  <input
                    type="number"
                    min="12"
                    max="72"
                    value={formData.fontSizeTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, fontSizeTitle: parseInt(e.target.value) || 36 }))}
                    disabled={saving}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={formData.titleColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, titleColor: e.target.value }))}
                      disabled={saving}
                      className="color-picker"
                    />
                    <input
                      type="text"
                      value={formData.titleColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, titleColor: e.target.value }))}
                      placeholder="#FFFFFF"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      disabled={saving}
                      className="color-hex-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* HEADING */}
            <div className="typography-item">
              <h4>Heading</h4>
              <div className="typography-controls">
                <div className="form-group">
                  <label>Size (pts)</label>
                  <input
                    type="number"
                    min="10"
                    max="48"
                    value={formData.fontSizeHeading}
                    onChange={(e) => setFormData(prev => ({ ...prev, fontSizeHeading: parseInt(e.target.value) || 28 }))}
                    disabled={saving}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={formData.headingColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, headingColor: e.target.value }))}
                      disabled={saving}
                      className="color-picker"
                    />
                    <input
                      type="text"
                      value={formData.headingColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, headingColor: e.target.value }))}
                      placeholder="#FFFFFF"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      disabled={saving}
                      className="color-hex-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* BODY */}
            <div className="typography-item">
              <h4>Body Text (Content)</h4>
              <div className="typography-controls">
                <div className="form-group">
                  <label>Size (pts)</label>
                  <input
                    type="number"
                    min="8"
                    max="36"
                    value={formData.fontSizeBody}
                    onChange={(e) => setFormData(prev => ({ ...prev, fontSizeBody: parseInt(e.target.value) || 16 }))}
                    disabled={saving}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={formData.bodyColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, bodyColor: e.target.value }))}
                      disabled={saving}
                      className="color-picker"
                    />
                    <input
                      type="text"
                      value={formData.bodyColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, bodyColor: e.target.value }))}
                      placeholder="#E5E5E5"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      disabled={saving}
                      className="color-hex-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* COVER BODY */}
            <div className="typography-item">
              <h4>Body Text (Cover)</h4>
              <div className="typography-controls">
                <div className="form-group">
                  <label>Size (pts)</label>
                  <span className="field-description">Uses body size</span>
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={formData.coverBodyColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, coverBodyColor: e.target.value }))}
                      disabled={saving}
                      className="color-picker"
                    />
                    <input
                      type="text"
                      value={formData.coverBodyColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, coverBodyColor: e.target.value }))}
                      placeholder="#FFFFFF"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      disabled={saving}
                      className="color-hex-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="typography-preview">
            <h4>Preview</h4>
            <div 
              className="preview-content" 
              style={{ fontFamily: formData.fontFamily }}
            >
              <div 
                style={{ 
                  fontSize: `${formData.fontSizeTitle}px`,
                  color: formData.titleColor,
                  marginBottom: '20px',
                  fontWeight: 'bold',
                  WebkitTextFillColor: formData.titleColor
                }}
              >
                Slide Title Example
              </div>
              <div 
                style={{ 
                  fontSize: `${formData.fontSizeHeading}px`,
                  color: formData.headingColor,
                  marginBottom: '16px',
                  fontWeight: 600,
                  WebkitTextFillColor: formData.headingColor
                }}
              >
                Heading Example
              </div>
              <div 
                style={{ 
                  fontSize: `${formData.fontSizeBody}px`,
                  color: formData.bodyColor,
                  lineHeight: 1.8,
                  WebkitTextFillColor: formData.bodyColor
                }}
              >
                • Body text example showing how your content will appear
                <br />
                • Multiple bullet points demonstrate spacing
                <br />
                • Easy to see the final result before saving
              </div>
            </div>
          </div>
        </div>

        {/* Background Images Section */}
        <div className="form-section">
          <h3>Background Images</h3>
          <p className="form-description">
            Upload custom backgrounds for each slide type (Max 10MB, JPEG/PNG/WebP)
          </p>

          {(['cover', 'split', 'text'] as const).map(type => (
            <div key={type} className="background-upload-group">
              <h4>{type.charAt(0).toUpperCase() + type.slice(1)} Slide Background</h4>
              
              {backgroundPreviews[type] && (
                <div className="background-preview">
                  <img src={backgroundPreviews[type]} alt={`${type} background`} />
                  <button
                    type="button"
                    onClick={() => handleRemoveBackground(type)}
                    className="remove-background-btn"
                    disabled={saving}
                  >
                    ×
                  </button>
                </div>
              )}

              <div className="upload-controls">
                <input
                  type="file"
                  id={`bg-${type}`}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handleBackgroundSelect(type, e)}
                  disabled={saving}
                  style={{ display: 'none' }}
                />
                <label htmlFor={`bg-${type}`} className="upload-button">
                  {backgroundPreviews[type] ? 'Change Background' : 'Upload Background'}
                </label>
                {pendingFiles[type] && (
                  <span className="pending-file-indicator">
                    📁 {pendingFiles[type]!.name} (pending upload)
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Messages */}
        {error && (
          <div className="message message-error">
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="message message-success">
            {successMessage}
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="btn-secondary"
            disabled={saving}
          >
            Reset
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save PowerPoint Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PowerPointSettingsForm;

